import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from './AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Mail, CheckCircle2, Lock, Copy, Eye, EyeOff } from 'lucide-react';
import { getPlanInfo } from '@/integrations/supabase/plan-utils';
import { getUsersFromEmpresa, createOperador } from '@/integrations/supabase/permissions';
import { enviarEmailBemvindoOperador } from '@/services/emailOperador';

interface DialogNovoOperadorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type AbaType = 'convidar' | 'criar';

export function DialogNovoOperador({ open, onOpenChange }: DialogNovoOperadorProps) {
  const { empresa } = useAuth();
  const empresaId = empresa?.id;
  const queryClient = useQueryClient();
  const [aba, setAba] = useState<AbaType>('convidar');

  // Aba Convidar
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [inviteSent, setInviteSent] = useState(false);

  // Aba Criar
  const [emailCriar, setEmailCriar] = useState('');
  const [fullNameCriar, setFullNameCriar] = useState('');
  const [senhaCriar, setSenhaCriar] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [operadorCriado, setOperadorCriado] = useState<{email: string; password: string} | null>(null);

  // Estado comum
  const [planInfo, setPlanInfo] = useState<any>(null);
  const [operadoresCount, setOperadoresCount] = useState(0);

  // Carregar info do plano e contar operadores
  useEffect(() => {
    if (open && empresaId) {
      Promise.all([
        getPlanInfo(empresaId),
        getUsersFromEmpresa(empresaId)
      ]).then(([plan, operadores]) => {
        setPlanInfo(plan);
        setOperadoresCount(operadores.length);
      }).catch(err => console.error('Erro ao carregar dados:', err));
    }
  }, [open, empresaId]);

  // Reset quando fecha
  useEffect(() => {
    if (!open) {
      setEmail('');
      setFullName('');
      setEmailCriar('');
      setFullNameCriar('');
      setSenhaCriar('');
      setInviteSent(false);
      setOperadorCriado(null);
      setMostrarSenha(false);
    }
  }, [open]);

  const verificarLimite = () => {
    if (!planInfo) throw new Error('Informações do plano não carregadas');
    const maxOperadores = planInfo.isPremium ? 3 : 1;
    if (operadoresCount >= maxOperadores) {
      const mensagem = maxOperadores === 1
        ? '❌ Seu plano permite apenas 1 operador. Faça upgrade para adicionar mais.'
        : `❌ Você atingiu o limite de ${maxOperadores} operadores.`;
      throw new Error(mensagem);
    }
  };

  const createMutationConvidar = useMutation({
    mutationFn: async () => {
      if (!empresaId) throw new Error('Empresa não encontrada');
      if (!empresa?.id) throw new Error('Dados da empresa não carregados');
      verificarLimite();

      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase(),
        password: crypto.getRandomValues(new Uint8Array(16)).toString(),
        options: {
          data: {
            full_name: fullName,
            empresa_id: empresaId,
          },
        },
      });

      if (error) throw new Error(error.message || 'Erro ao registrar operador');

      if (data.user?.id) {
        // 🔗 Associar operador à empresa com permissões padrão
        try {
          await createOperador(empresaId, data.user.id, 'editor');
          console.log('✅ Operador criado com permissões padrão');
        } catch (err) {
          console.error('❌ Erro ao criar operador com permissões:', err);
          throw new Error('Falha ao configurar permissões do operador');
        }

        // Enviar email de convite
        enviarEmailBemvindoOperador({
          to: email.toLowerCase(),
          fullName,
          empresaNome: empresa?.nome || 'Ateliê Pro',
          linkCadastro: `${window.location.origin}/login`,
        }).catch(err => console.warn('Email falhou:', err));
      }

      return { userId: data.user?.id, email };
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['operadores', empresaId] });
      setInviteSent(true);
      toast.success('Operador criado! Email de confirmação enviado.');
      setTimeout(() => {
        setEmail('');
        setFullName('');
        setInviteSent(false);
        onOpenChange(false);
      }, 3000);
    },
    onError: (error) => {
      toast.error(`Erro: ${(error as any).message}`);
    }
  });

  const createMutationDireto = useMutation({
    mutationFn: async () => {
      if (!empresaId) throw new Error('Empresa não encontrada');
      if (!empresa?.id) throw new Error('Dados da empresa não carregados');
      verificarLimite();

      const { data, error } = await supabase.auth.signUp({
        email: emailCriar.toLowerCase(),
        password: senhaCriar,
        options: {
          data: {
            full_name: fullNameCriar,
            empresa_id: empresaId,
          },
        },
      });

      if (error) throw new Error(error.message || 'Erro ao criar operador');

      if (data.user?.id) {
        // 🔗 Associar operador à empresa com permissões padrão
        try {
          await createOperador(empresaId, data.user.id, 'editor');
          console.log('✅ Operador criado com permissões padrão');
        } catch (err) {
          console.error('❌ Erro ao criar operador com permissões:', err);
          throw new Error('Falha ao configurar permissões do operador');
        }
      }

      return {
        userId: data.user?.id,
        email: emailCriar,
        password: senhaCriar
      };
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['operadores', empresaId] });
      setOperadorCriado({ email: data.email, password: data.password });
      toast.success('Operador criado com sucesso!');
    },
    onError: (error) => {
      toast.error(`Erro: ${(error as any).message}`);
    }
  });

  const copiarCredenciais = async () => {
    if (!operadorCriado) return;
    const texto = `Email: ${operadorCriado.email}\nSenha: ${operadorCriado.password}`;
    await navigator.clipboard.writeText(texto);
    toast.success('Credenciais copiadas!');
  };

  // Tela de sucesso - Aba Convidar
  if (inviteSent) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="text-center space-y-4">
          <div className="flex justify-center">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold">Convite Enviado!</h2>
          <p className="text-gray-600">
            Um email de confirmação foi enviado para <strong>{email}</strong>
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Operador recebe email</li>
              <li>Clica no link para confirmar</li>
              <li>Cria sua própria senha</li>
              <li>Acessa a plataforma</li>
            </ol>
          </div>
          <p className="text-xs text-gray-500">Fechando em 3 segundos...</p>
        </DialogContent>
      </Dialog>
    );
  }

  // Tela de sucesso - Aba Criar
  if (operadorCriado) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="text-center space-y-4">
          <div className="flex justify-center">
            <CheckCircle2 className="w-16 h-16 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold">Operador Criado!</h2>
          <p className="text-gray-600">
            Compartilhe essas credenciais com o operador
          </p>

          <div className="bg-gray-100 rounded-lg p-4 space-y-2 text-left">
            <div>
              <p className="text-xs text-gray-600 mb-1">Email</p>
              <p className="font-mono text-sm">{operadorCriado.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-600 mb-1">Senha</p>
              <p className="font-mono text-sm">{operadorCriado.password}</p>
            </div>
          </div>

          <Button
            onClick={copiarCredenciais}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copiar Credenciais
          </Button>

          <p className="text-xs text-gray-500">
            O operador pode fazer login agora em /login
          </p>

          <Button
            onClick={() => {
              setOperadorCriado(null);
              setEmailCriar('');
              setFullNameCriar('');
              setSenhaCriar('');
              onOpenChange(false);
            }}
            className="w-full"
          >
            Fechar
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Novo Operador</DialogTitle>
        </DialogHeader>

        {/* Abas */}
        <div className="flex gap-2 border-b mb-4">
          <button
            onClick={() => setAba('convidar')}
            className={`pb-2 px-4 font-medium text-sm ${
              aba === 'convidar'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Mail className="w-4 h-4 inline mr-2" />
            Convidar por Email
          </button>
          <button
            onClick={() => setAba('criar')}
            className={`pb-2 px-4 font-medium text-sm ${
              aba === 'criar'
                ? 'border-b-2 border-purple-600 text-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Lock className="w-4 h-4 inline mr-2" />
            Criar Direto
          </button>
        </div>

        {/* Alerta de limite */}
        {planInfo && (
          <div className={`border rounded p-3 text-sm space-y-2 ${
            operadoresCount >= (planInfo.isPremium ? 3 : 1)
              ? 'bg-red-50 border-red-200'
              : 'bg-amber-50 border-amber-200'
          }`}>
            <p className={`font-semibold flex items-center gap-2 ${
              operadoresCount >= (planInfo.isPremium ? 3 : 1)
                ? 'text-red-800'
                : 'text-amber-800'
            }`}>
              👥 Operadores: {operadoresCount}/{planInfo.isPremium ? '3' : '1'}
            </p>
            <p className={operadoresCount >= (planInfo.isPremium ? 3 : 1) ? 'text-red-700' : 'text-amber-700'}>
              {operadoresCount >= (planInfo.isPremium ? 3 : 1)
                ? planInfo.isPremium
                  ? 'Limite atingido. Remova um operador ou faça upgrade.'
                  : 'Plano Básico permite apenas 1 operador. Faça upgrade para adicionar mais.'
                : `Você pode adicionar ${(planInfo.isPremium ? 3 : 1) - operadoresCount} operador(es) ainda.`}
            </p>
          </div>
        )}

        {/* ABA CONVIDAR */}
        {aba === 'convidar' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="fullName">Nome Completo</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Ex: João Silva"
                disabled={createMutationConvidar.isPending}
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="joao@exemplo.com"
                disabled={createMutationConvidar.isPending}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
              <p className="font-semibold mb-2">Como funciona:</p>
              <ol className="list-decimal list-inside text-xs space-y-1">
                <li>Operador recebe email de confirmação</li>
                <li>Clica no link para confirmar</li>
                <li>Cria sua própria senha</li>
                <li>Acessa a plataforma</li>
              </ol>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutationConvidar.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => createMutationConvidar.mutate()}
                disabled={
                  !email || !fullName || createMutationConvidar.isPending ||
                  !planInfo || operadoresCount >= (planInfo?.isPremium ? 3 : 1)
                }
              >
                {createMutationConvidar.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enviar Convite
              </Button>
            </div>
          </div>
        )}

        {/* ABA CRIAR */}
        {aba === 'criar' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="fullNameCriar">Nome Completo</Label>
              <Input
                id="fullNameCriar"
                value={fullNameCriar}
                onChange={(e) => setFullNameCriar(e.target.value)}
                placeholder="Ex: João Silva"
                disabled={createMutationDireto.isPending}
              />
            </div>

            <div>
              <Label htmlFor="emailCriar">Email</Label>
              <Input
                id="emailCriar"
                type="email"
                value={emailCriar}
                onChange={(e) => setEmailCriar(e.target.value)}
                placeholder="joao@exemplo.com"
                disabled={createMutationDireto.isPending}
              />
            </div>

            <div>
              <Label htmlFor="senha">Senha</Label>
              <div className="relative">
                <Input
                  id="senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  value={senhaCriar}
                  onChange={(e) => setSenhaCriar(e.target.value)}
                  placeholder="Digite uma senha segura"
                  disabled={createMutationDireto.isPending}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-3 top-2.5 text-gray-500"
                >
                  {mostrarSenha ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
              <p className="font-semibold mb-2">Como funciona:</p>
              <ol className="list-decimal list-inside text-xs space-y-1">
                <li>Operador é criado com a senha que você definir</li>
                <li>Compartilhe as credenciais (email e senha)</li>
                <li>Operador acessa direto em /login</li>
              </ol>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={createMutationDireto.isPending}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => createMutationDireto.mutate()}
                disabled={
                  !emailCriar || !fullNameCriar || !senhaCriar || createMutationDireto.isPending ||
                  !planInfo || operadoresCount >= (planInfo?.isPremium ? 3 : 1)
                }
              >
                {createMutationDireto.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Criar Operador
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}